import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { useState, useRef, useCallback, useEffect } from "react";
import type { paths } from "../../api/generated/api-types";
import { logger } from "../../observability/LoggerFactory";

type ListProxiesResponse =
  paths["/proxies"]["get"]["responses"]["200"]["content"]["application/json"];

export type ProxyListItem = ListProxiesResponse["items"][number];

export type ProxyStatusFilter =
  NonNullable<paths["/proxies"]["get"]["parameters"]["query"]>["status"];

type ScreenState =
  | { kind: "loading" }
  | { kind: "empty" }
  | { kind: "data"; items: ProxyListItem[]; totalCount: number }
  | { kind: "error"; message: string };

const DEFAULT_PAGE_SIZE = 25;

export type ProxyGroupFilter =
  NonNullable<paths["/proxies"]["get"]["parameters"]["query"]>["group"];

export interface UseProxyListResult {
  state: ScreenState;
  fetching: boolean;
  page: number;
  pageSize: number;
  filterStatus: ProxyStatusFilter;
  filterGroup: ProxyGroupFilter;
  setFilterStatus: (status: ProxyStatusFilter) => void;
  setFilterGroup: (group: ProxyGroupFilter) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  refetch: () => void;
}

export function useProxyList(): UseProxyListResult {
  const [state, setState] = useState<ScreenState>({ kind: "loading" });
  const [fetching, setFetching] = useState(false);
  const [filterStatus, setFilterStatusState] = useState<ProxyStatusFilter>(undefined);
  const [filterGroup, setFilterGroupState] = useState<ProxyGroupFilter>(undefined);
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const abortRef = useRef<AbortController | null>(null);
  const initialLoadDone = useRef(false);

  const fetchProxies = useCallback(
    async (
      currentStatus: ProxyStatusFilter,
      currentGroup: ProxyGroupFilter,
      currentPage: number,
      currentPageSize: number,
    ) => {
      if (abortRef.current !== null) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;

      if (!initialLoadDone.current) {
        setState({ kind: "loading" });
      }

      setFetching(true);

      const parameters = new URLSearchParams();
      parameters.set("page", String(currentPage));
      parameters.set("pageSize", String(currentPageSize));
      if (currentStatus !== undefined) {
        parameters.set("status", currentStatus);
      }
      if (currentGroup !== undefined) {
        parameters.set("group", currentGroup);
      }

      try {
        const response = await fetchWithRefresh(`/api/proxies?${parameters.toString()}`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = (await response.json()) as ListProxiesResponse;

        initialLoadDone.current = true;

        if (data.items.length === 0 && data.totalCount === 0) {
          setState({ kind: "empty" });
        } else {
          setState({
            kind: "data",
            items: data.items,
            totalCount: data.totalCount,
          });
        }
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        const message = error instanceof Error ? error.message : "Unknown error";
        logger.error({ err: message }, "Failed to fetch proxies");
        setState({ kind: "error", message });
      } finally {
        if (!controller.signal.aborted) {
          setFetching(false);
        }
      }
    },
    [],
  );

  const filterStatusRef = useRef(filterStatus);
  filterStatusRef.current = filterStatus;
  const filterGroupRef = useRef(filterGroup);
  filterGroupRef.current = filterGroup;
  const pageRef = useRef(page);
  pageRef.current = page;
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;

  useEffect(() => {
    void fetchProxies(filterStatus, filterGroup, page, pageSize);
    return () => {
      if (abortRef.current !== null) {
        abortRef.current.abort();
      }
    };
  }, [filterStatus, filterGroup, page, pageSize, fetchProxies]);

  const refetch = useCallback(() => {
    void fetchProxies(filterStatusRef.current, filterGroupRef.current, pageRef.current, pageSizeRef.current);
  }, [fetchProxies]);

  const setFilterStatus = useCallback((status: ProxyStatusFilter) => {
    setFilterStatusState(status);
    setPageState(1);
  }, []);

  const setFilterGroup = useCallback((group: ProxyGroupFilter) => {
    setFilterGroupState(group);
    setPageState(1);
  }, []);

  const setPage = useCallback((newPage: number) => {
    setPageState(newPage);
  }, []);

  const setPageSize = useCallback((newPageSize: number) => {
    setPageSizeState(newPageSize);
    setPageState(1);
  }, []);

  return {
    state,
    fetching,
    page,
    pageSize,
    filterStatus,
    filterGroup,
    setFilterStatus,
    setFilterGroup,
    setPage,
    setPageSize,
    refetch,
  };
}
